import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { ArrowLeft, Building2, Info, ShieldCheck, TrendingUp } from "lucide-react";

function Pct({ value }) {
  const pct = Math.round((value || 0) * 100);
  const tone = pct >= 90 ? "text-emerald-600" : pct >= 70 ? "text-slate-900" : "text-orange-600";
  return <span className={cn("font-mono font-semibold", tone)}>{pct}%</span>;
}

/**
 * Public marketplace org profile. Deliberately does NOT reuse the individual
 * employee Leaderboard component — org rank and personal reputation must
 * stay visually and structurally separate (PRD gap #18).
 */
export default function OrgProfile() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/marketplace/orgs/${orgId}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [orgId]);

  if (loading) return <p className="text-slate-500 text-sm max-w-3xl mx-auto">Loading…</p>;
  if (!data) return null;

  const stats = data.marketplace_rank_by_category;
  const bestRank = stats.length ? Math.max(...stats.map((s) => s.rank_score)) : null;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="rounded-3xl bg-gradient-to-br from-[#0b1e3d] via-[#0f2a52] to-[#123a6e] text-white p-8 sm:p-10 shadow-xl shadow-blue-950/10 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-blue-400/10 blur-3xl" />
        <div className="relative flex items-start justify-between gap-6 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center shrink-0">
              <Building2 className="h-8 w-8 text-blue-200" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-display text-3xl font-bold tracking-tighter">{data.org.name}</h1>
                {data.org.is_premium && (
                  <Badge className="bg-white/15 text-white border border-white/25 gap-1">
                    <ShieldCheck className="h-3 w-3" /> Verified · Premium
                  </Badge>
                )}
              </div>
              <p className="text-blue-200/70 text-sm mt-1">Marketplace vendor profile</p>
            </div>
          </div>
          {bestRank != null && (
            <div className="text-right">
              <div className="flex items-center gap-1.5 text-blue-200/70 text-xs justify-end mb-1">
                <TrendingUp className="h-3.5 w-3.5" /> Best rank score
              </div>
              <div className="font-mono text-3xl font-bold">{bestRank}</div>
            </div>
          )}
        </div>
      </div>

      {data.j_score_context != null && (
        <Card className="border-slate-200 bg-slate-50">
          <CardContent className="p-4 flex items-start gap-3">
            <Info className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
            <p className="text-sm text-slate-600">
              <span className="font-semibold text-slate-900">J-Score context: {data.j_score_context}</span>
              {" — "}an internal-activity trust signal, shown here for context only. It is never
              blended into the marketplace rank below.
            </p>
          </CardContent>
        </Card>
      )}

      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">Marketplace rank by category</h2>
        {stats.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 py-12 text-center">
            <p className="text-sm text-slate-500">No completed marketplace tenders yet.</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {stats.map((s) => (
              <Card key={s.category} className="border-slate-200 shadow-sm" data-testid={`rank-${s.category}`}>
                <CardContent className="p-5 flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-slate-900">{s.category}</div>
                    <div className="text-xs text-slate-500 mt-1">
                      completion <Pct value={s.completion_rate} /> · on-time <Pct value={s.on_time_rate} />
                      {s.dispute_rate > 0 && <span className="text-orange-600"> · dispute rate {Math.round(s.dispute_rate * 100)}%</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-xl font-bold text-slate-900">{s.rank_score}</div>
                    <div className="text-[10px] uppercase tracking-wide text-slate-400">rank score</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
